<template>
  <x-form :submit-text="$t('Invia')" :submit-help="`* ${$t('Campi obbligatorii')}`" @submit="send" style="max-width: 80ch">
    <template v-if="askSiteTransfer">
      <pf-title h="4">{{
        $t('Per poter effettuare il trasferimento del sito {0}, come richiesto, abbiamo bisogno di queste informazioni e dati di accesso.', [transfer.dominio])
      }}</pf-title>

      <form-group :name="$t('Tipo di accesso')" required>
        <p>{{ $t("Indicaci le modalità di accesso all'attuale provider di hosting") }}</p>
        <pf-form-select
          :model-value="transferData.access_type"
          @update:model-value="transferData.access_type = $as<HostingTransfer['access_type']>($event)"
          name="access_type"
          required
        >
          <pf-form-select-option v-for="(access, key) in accessTypes" :key="key" :value="key">
            {{ access }}
          </pf-form-select-option>
        </pf-form-select>
      </form-group>

      <form-group :name="transferData.access_type === 'ftp' || transferData.access_type === 'ssh' ? $t('Host') : $t('Url')" required>
        <p v-if="transferData.access_type === 'ftp' || transferData.access_type === 'ssh'">
          {{ $t("Indicaci l'indirizzo IP o il dominio per effettuare l'accesso") }}
        </p>
        <p v-else>{{ $t("Indicaci l'URL per accedere al pannello") }}</p>
        <x-input v-model="transferData.url" name="url" required />
      </form-group>

      <form-group :name="$t('Utente')" required>
        <x-input v-model="transferData.user" name="user" required />
      </form-group>

      <form-group :name="$t('Password')" required>
        <x-input v-model="transferData.password" type="password" name="password" required />
      </form-group>

      <pf-title h="3">{{ $t('Database') }}</pf-title>
      <form-group :name="$t('Nome database')">
        <x-input v-model="transferData.dbName" name="dbName" />
      </form-group>

      <form-group :name="$t('Utente database')">
        <x-input v-model="transferData.dbUser" name="dbUser" />
      </form-group>

      <form-group :name="$t('Password database')">
        <x-input v-model="transferData.dbPassword" type="password" name="dbPassword" />
      </form-group>

      <form-group :name="$t('URL di phpMyAdmin')">
        <x-input v-model="transferData.pmaUrl" name="pmaUrl" />
      </form-group>

      <form-group :name="$t('CMS')">
        <pf-radio v-for="c in cms" :checked="transferData.cms === c" @change="transferData.cms = c" :value="c" :label="c" name="cms" />
      </form-group>

      <form-group :name="$t('Manutenzione')" required>
        <p>
          {{
            $t(
              'Per procedere con il trasferimento del sito, è necessario accordarsi sulle tempistiche e mettere il sito in manutenzione, o possiamo procedere in qualsiasi momento e senza preavviso?',
            )
          }}
        </p>
        <pf-radio
          :checked="transferData.maintenanceSite === true"
          @change="transferData.maintenanceSite = true"
          name="maintenanceSite"
          required
          :label="$t('Sì, dobbiamo accordarci')"
        />
        <pf-radio
          :checked="transferData.maintenanceSite === false"
          @change="transferData.maintenanceSite = false"
          name="maintenanceSite"
          required
          :label="$t('No, procedete pure in qualsiasi momento')"
        />
      </form-group>

      <form-group :name="$t('Sottodomini e sottocartella')">
        <p>{{ $t('Indicaci tutti gli eventuali sottodomini e sottocartelle da trasferire') }}</p>
        <pf-textarea :model-value="transferData.subdomains" @update:model-value="transferData.subdomains = String($event)" name="subdomains" :rows="5" />
        <template #helper-text>{{
          $t('IMPORTANTE: Accertati di aver inserito la lista completa di sottodomini e cartelle da trasferire per evitare la perdita di dati importanti.')
        }}</template>
      </form-group>
    </template>

    <template v-if="askMailTransfer">
      <pf-title h="4">{{
        $t('Per poter effettuare il trasferimento della posta del dominio {0}, come richiesto, abbiamo bisogno di queste informazioni.', [transfer.dominio])
      }}</pf-title>

      <form-group :name="$t('Server IMAP')" required>
        <x-input name="imap" v-model="transferData.imap" required :placeholder="$t('Server IMAP')" />
      </form-group>

      <form-group :name="$t('Caselle email')" required>
        <p>{{ $t('Inserisci tutte le caselle di posta elettronica che dobbiamo trasferire, con le relative password') }}</p>

        <pf-flex direction="column" gap="md">
          <pf-flex v-for="(mail, index) in transferData.mails" gap="md" row-gap="sm">
            <pf-flex-item grow>
              <x-input :name="`box-${index + 1}`" v-model="mail.name" required :placeholder="$t('Casella')" :append="`@${transfer.dominio}`" />
            </pf-flex-item>

            <pf-flex-item grow>
              <x-input :name="`password-${index + 1}`" type="password" v-model="mail.password" required :placeholder="$t('Password')" />
            </pf-flex-item>

            <pf-flex-item style="min-width: 11ch">
              <pf-button v-if="index > 1 || transferData.mails.length > 1" @click="removeMail(index)" small variant="danger" :title="$t('Rimuovi casella')">
                <template #icon><trash-icon /></template>
              </pf-button>

              <pf-button
                ouia-id="new-email-box"
                v-if="index === transferData.mails.length - 1"
                variant="secondary"
                @click="addMail()"
                small
                :title="$t('Aggiungi casella')"
                class="pf-v6-u-ml-xs"
              >
                <template #icon><plus-icon /></template>
              </pf-button>
            </pf-flex-item>
          </pf-flex>
        </pf-flex>

        <template #helper-text>
          {{ $t('IMPORTANTE: Accertati di aver inserito tutte le caselle da trasferire per evitare la perdita di dati importanti.') }}
        </template>
      </form-group>
    </template>

    <form-group :name="$t('Note')">
      <p>{{ $t('Scrivi qui tutte le altre informazioni che possono esserci utili per trasferire il sito') }}</p>
      <pf-textarea :model-value="transferData.note" @update:model-value="transferData.note = String($event)" name="note" :rows="5" />
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { type Ref, ref, computed } from 'vue';
import { type Transfer, type HostingTransferAccessType, type HostingTransfer, HostingTransferResource } from '@/resources';
import TrashIcon from '@vue-patternfly/icons/trash-icon';
import PlusIcon from '@vue-patternfly/icons/plus-icon';

const props = defineProps<{
  transfer: Transfer;
}>();

const emit = defineEmits<{
  (e: 'submit'): void;
}>();

const askMailTransfer = computed(() => props.transfer.type === 'mail' || props.transfer.type === 'site-mail');
const askSiteTransfer = computed(() => props.transfer.type === 'site' || props.transfer.type === 'site-mail');

const cms = ['WordPress', 'Magento', 'PrestaShop', 'Joomla', _t('Altro')];

type HostingTransferForm = Omit<HostingTransfer, 'maintenanceSite'> & {
  maintenanceSite: boolean | undefined;
};

const accessTypes: Record<HostingTransferAccessType, string> = {
  ftp: _t('Accesso tramite FTP'),
  ssh: _t('Accesso tramite SSH'),
  panel: _t('Accesso al pannello di controllo (esp. cPanel, WHM, Plesk...)'),
  webaccess: _t('Accesso web (esp. Aruba, Register...)'),
};

const transferData: Ref<HostingTransferForm> = ref({
  id: props.transfer.id,
  access_type: askSiteTransfer.value ? 'ftp' : null,
  url: '',
  user: '',
  password: '',
  dbName: '',
  dbUser: '',
  dbPassword: '',
  pmaUrl: '',
  cms: '',
  maintenanceSite: undefined,
  subdomains: '',
  imap: '',
  mails: [
    {
      name: '',
      password: '',
    },
  ],
  note: '',
});

async function send() {
  if (
    !confirm(
      _t(
        'Stai per inviare la richiesta di trasferimento del sito e della posta elettronica. Assicurati di aver inserito tutti i dati necessari in modo corretto e completo. Confermi di voler inviare i dati inseriti?',
      ),
    )
  ) {
    return;
  }

  await new HostingTransferResource().post(transferData.value);
  emit('submit');
}

function addMail() {
  transferData.value.mails.push({
    name: '',
    password: '',
  });
}

function removeMail(index: number) {
  transferData.value.mails.splice(index, 1);
}
</script>
